import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  Packer,
} from "docx";

const BLUE = "1410DF";

const heading = (text, level = HeadingLevel.HEADING_2) =>
  new Paragraph({
    heading: level,
    spacing: { before: 360, after: 120 },
    children: [new TextRun({ text, bold: true, size: level === HeadingLevel.HEADING_1 ? 28 : 24, color: level === HeadingLevel.HEADING_1 ? "111827" : BLUE })],
  });

const sectionTitle = (text) =>
  new Paragraph({
    spacing: { before: 400, after: 160 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 3, color: BLUE } },
    children: [new TextRun({ text: text.toUpperCase(), bold: true, size: 22, color: "111827" })],
  });

const p = (text, opts = {}) =>
  new Paragraph({
    spacing: { after: 120 },
    alignment: opts.center ? AlignmentType.CENTER : AlignmentType.JUSTIFIED,
    children: [new TextRun({ text, size: 22, bold: opts.bold, italics: opts.italic, color: opts.color || "1f2937" })],
  });

const bullet = (text) =>
  new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 60 },
    children: [new TextRun({ text, size: 22 })],
  });

const cellBorder = {
  top: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
  left: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
  right: { style: BorderStyle.SINGLE, size: 1, color: "d1d5db" },
};

const tableRow = (label, value, headerBg = false) =>
  new TableRow({
    children: [
      new TableCell({
        width: { size: 40, type: WidthType.PERCENTAGE },
        borders: cellBorder,
        shading: { fill: "f9fafb" },
        children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 22 })] })],
      }),
      new TableCell({
        width: { size: 60, type: WidthType.PERCENTAGE },
        borders: cellBorder,
        children: [new Paragraph({ children: [new TextRun({ text: value, size: 22 })] })],
      }),
    ],
  });

const annexHeaderRow = () =>
  new TableRow({
    children: ["Concepto", "Detalle"].map(
      (text) =>
        new TableCell({
          borders: cellBorder,
          shading: { fill: BLUE },
          children: [new Paragraph({ children: [new TextRun({ text, bold: true, size: 22, color: "FFFFFF" })] })],
        })
    ),
  });

export const generateAgreementDocx = async (formData) => {
  const modalidadTexto =
    formData.modalidad === "porcentual"
      ? "Comisión Porcentual (20%)"
      : "Compra al Por Mayor";

  const doc = new Document({
    numbering: {
      config: [{ reference: "bullets", levels: [{ level: 0, format: "bullet", text: "•", alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } }] }],
    },
    sections: [
      {
        properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 60 },
            children: [new TextRun({ text: "KUSINA | Acuerdo Comercial de Cooperación", bold: true, size: 32, color: "111827" })],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 320 },
            children: [new TextRun({ text: "Versión 1.0 · Vigencia indefinida", size: 20, color: "6b7280" })],
          }),

          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              tableRow("Documento", "Acuerdo Comercial de Cooperación"),
              tableRow("Versión", "1.0"),
              tableRow("Vigencia", "Indefinida"),
            ],
          }),

          sectionTitle("Datos Legales de Kusina"),
          p("Razón Social: KUSINA APP E.I.R.L."),
          p("Nombre Comercial: KUSINA APP"),
          p("RUC: 20613753983"),
          p("Tipo: Empresa Individual de Responsabilidad Limitada"),
          p("Domicilio Fiscal: Av. Javier Prado Este Nro. 560 Int. 1302, San Isidro, Lima, Perú"),
          p("Actividad Económica: Programación Informática (CIIU 6201)"),
          p("Correo: soporte@kusina.site"),
          p("Teléfono: +51 973 834 813"),

          sectionTitle("Preámbulo"),
          p('El presente acuerdo se celebra entre KUSINA APP E.I.R.L. (en adelante, "Kusina"), plataforma tecnológica domiciliada en Perú, y el restaurante o establecimiento de alimentos suscriptor (en adelante, "el Aliado Comercial" o "el Restaurante"), dentro del área de cobertura de la plataforma.'),
          p('Las partes se denominan conjuntamente como "las Partes" e individualmente como "la Parte".'),

          sectionTitle("Cláusula Primera: Definiciones"),
          heading("1.1 Plataforma Kusina"),
          p("Plataforma tecnológica compuesta por aplicaciones móviles y sistemas web que facilitan la exhibición, comercialización y gestión de pedidos."),
          heading("1.2 Kusina P.O.S"),
          p("Módulo del sistema de punto de venta para gestión operativa: toma de pedidos, facturación y control de órdenes."),
          heading("1.3 Kusina Delivery"),
          p("Módulo orientado a consumidores finales para compra y entrega de alimentos."),
          heading("1.4 Eater / Usuario"),
          p("Persona natural que utiliza la plataforma para adquirir productos alimenticios."),
          heading("1.5 Orden / Pedido"),
          p("Solicitud de compra realizada por un Eater a través de la plataforma o en el establecimiento."),
          heading("1.6 Establecimiento"),
          p("Local comercial donde se preparan y/o expenden los productos."),
          heading("1.7 Comisión"),
          p("Porcentaje sobre el valor de cada orden que el restaurante reconoce a Kusina por el uso de la plataforma."),
          heading("1.8 Pooler"),
          p("Representante de Kusina encargado de la integración y acompañamiento del Aliado Comercial."),

          sectionTitle("Cláusula Segunda: Objeto del Acuerdo y Naturaleza de la Intermediación"),
          heading("2.1"),
          p("Kusina proporciona a los Aliados Comerciales acceso a su plataforma tecnológica para que exhiban, ofrezcan y gestionen la comercialización de sus productos alimenticios frente a los Eaters."),
          heading("2.2"),
          p('Kusina actúa exclusivamente como intermediario tecnológico entre el Aliado Comercial y los Eaters, facilitando la conexión entre ambos a través de herramientas digitales. En ningún caso Kusina adquiere la condición de productor, fabricante, proveedor, vendedor ni co-contratante en la relación de consumo entre el Aliado Comercial y el Eater. La relación contractual de compraventa de alimentos se perfecciona directa y exclusivamente entre el Aliado Comercial y el Eater.'),
          heading("2.3"),
          p("En virtud de lo señalado en la cláusula 2.2, la obligación de emitir los comprobantes de pago que correspondan por cada transacción de venta —sean boletas de venta electrónicas o facturas— recae única y exclusivamente sobre el Aliado Comercial, en su calidad de contribuyente y parte vendedora, conforme a lo establecido en el Reglamento de Comprobantes de Pago aprobado por Resolución de Superintendencia N.º 007-99/SUNAT y sus modificatorias. Kusina no emite, no está obligada a emitir, ni es responsable de la emisión de comprobantes de pago a los consumidores finales."),
          heading("2.4"),
          p("El Aliado Comercial hace uso de Kusina P.O.S para gestionar órdenes locales y de plataforma, con acceso a Kusina Delivery para la modalidad de entregas a domicilio."),

          sectionTitle("Cláusula Tercera: Comisiones y Estructura de Cobro"),
          heading("3.1 Modalidades de Retribución"),
          p("El Aliado Comercial operará bajo una de las siguientes modalidades, según lo pactado en el Anexo A suscrito al momento de la afiliación:"),
          heading("3.1.1 Modalidad por Comisión Porcentual"),
          p("El Aliado Comercial reconocerá a Kusina una comisión fija del veinte por ciento (20%) sobre el valor total de cada Orden procesada, impuestos incluidos, para los siguientes tipos de servicio:"),
          bullet("Recojo en local (pickup)"),
          bullet("Consumo en local (dine-in)"),
          bullet("Entrega a domicilio (delivery)"),
          heading("3.1.2 Modalidad de Compra al Por Mayor"),
          p("Kusina adquirirá del Aliado Comercial un volumen determinado de productos a un precio mayorista preferencial, pactado de mutuo acuerdo y registrado en el Anexo A. Kusina comercializará dichos productos al consumidor final al precio de venta al público fijado por el Aliado Comercial. La diferencia entre el precio mayorista y el precio de venta al público constituye la retribución de Kusina por sus servicios de intermediación."),
          heading("3.2 Base de Cálculo"),
          p("En la Modalidad por Comisión Porcentual, la comisión se aplica sobre el precio total cobrado al Eater, tal como figura en la plataforma al momento de la orden, incluyendo el Impuesto General a las Ventas (IGV) u otros tributos que correspondan. Quedan excluidos de la base de cálculo únicamente el costo de envío cobrado directamente al Eater y las propinas voluntarias."),
          heading("3.3 Obligación de Facturación a Precio de Venta al Público"),
          p("En ambas modalidades, el Aliado Comercial deberá emitir el comprobante de pago (boleta de venta o factura) al consumidor final consignando el precio de venta al público, conforme al Reglamento de Comprobantes de Pago (R.S. 007-99/SUNAT). En la Modalidad de Compra al Por Mayor, aun cuando Kusina adquiera los productos a un precio mayorista preferencial, el Aliado Comercial emitirá el comprobante al Eater por el precio completo de venta al público, dado que la relación de consumo se perfecciona entre el Aliado Comercial y el Eater. La diferencia entre ambos precios constituye un descuento comercial cuya carga tributaria ante la SUNAT es asumida por Kusina."),
          heading("3.4"),
          p("La modalidad específica y los precios mayoristas aplicables (en caso de la Modalidad de Compra al Por Mayor) se determinan al momento de la afiliación y quedan registrados en el Anexo A del presente acuerdo."),
          heading("3.5"),
          p("Kusina podrá revisar y ajustar la comisión o los precios mayoristas con un preaviso mínimo de treinta (30) días calendario notificado al Aliado Comercial."),

          sectionTitle("Cláusula Cuarta: Pagos, Liquidación y Facturación del Servicio"),
          heading("4.0 Alcance"),
          p("Los períodos de liquidación, cortes de pago y facturación descritos en la presente cláusula aplican exclusivamente a los Aliados Comerciales que operen bajo la Modalidad por Comisión Porcentual (Cláusula 3.1.1). Para los Aliados Comerciales que operen bajo la Modalidad de Compra al Por Mayor (Cláusula 3.1.2), Kusina realiza el pago al momento de la adquisición de los productos, por lo que no aplica el esquema de liquidación semanal ni la facturación por servicio de intermediación prevista en esta cláusula."),
          heading("4.1 Cortes de Liquidación"),
          p("Kusina consolida las ventas del Aliado Comercial en dos cortes semanales:"),
          bullet("Corte 1: Ventas generadas de lunes a miércoles — el depósito del monto neto se efectúa el viernes de la misma semana."),
          bullet("Corte 2: Ventas generadas de jueves a domingo — el depósito del monto neto se efectúa el miércoles de la semana siguiente."),
          heading("4.2 Factura de Kusina por Servicio de Intermediación"),
          p("Al cierre de cada período de liquidación, KUSINA APP E.I.R.L. emitirá una factura electrónica al Aliado Comercial por concepto de «Servicio de Intermediación Tecnológica», por el monto equivalente a la comisión acumulada en dicho período conforme a la Cláusula Tercera. El depósito del monto neto al Aliado Comercial se realizará simultáneamente o con posterioridad inmediata a la emisión de dicha factura, deducida la comisión correspondiente. El Aliado Comercial no deberá emitir comprobante alguno a Kusina por este concepto."),
          heading("4.3 Contenido de la Liquidación"),
          p("Cada liquidación detallará:"),
          bullet("Total de órdenes procesadas en el período"),
          bullet("Valor bruto de ventas"),
          bullet("Comisión por servicio de intermediación descontada"),
          bullet("Deducciones por cancelaciones y devoluciones aplicables"),
          bullet("Monto neto a depositar al Aliado Comercial"),
          heading("4.4 Plazo de Reclamo"),
          p("El Aliado Comercial dispone de cinco (5) días hábiles desde la recepción de la liquidación para formular observaciones. Transcurrido dicho plazo sin pronunciamiento, la liquidación se tendrá por aceptada de forma tácita."),
          heading("4.5 Datos Bancarios"),
          p("Es responsabilidad exclusiva del Aliado Comercial mantener actualizados sus datos bancarios en la plataforma. Kusina no asume responsabilidad por depósitos realizados en cuentas desactualizadas por negligencia del Aliado."),

          sectionTitle("Cláusula Quinta: Cancelaciones y Devoluciones"),
          heading("5.1 Cancelación Tardía por el Aliado Comercial"),
          p("Si el Aliado Comercial cancela una orden confirmada dentro de los cinco (5) minutos previos a la hora de entrega pactada, el personal de Kusina quedará facultado para negociar directamente con el consumidor final la retribución que corresponda. Dicha retribución consistirá, como mínimo, en el reembolso del valor del servicio de delivery de la orden cancelada, el cual será cargado al Aliado Comercial en la liquidación del período."),
          heading("5.2 Error en el Pedido"),
          p("Si el Aliado Comercial entrega un pedido erróneo, con productos distintos a los solicitados, en cantidades incorrectas o con omisiones, el costo total del producto será asumido íntegramente por el Aliado Comercial. Kusina gestionará con el Eater la reposición o devolución correspondiente, y el monto será descontado de la liquidación del período."),
          heading("5.3"),
          p("Son cancelaciones atribuibles al restaurante, entre otras:"),
          bullet("Productos agotados o no disponibles"),
          bullet("Demora excesiva en la preparación"),
          bullet("Productos en mal estado"),
          bullet("Errores en el pedido"),
          bullet("Incumplimiento de estándares de calidad"),
          heading("5.4"),
          p("Los descuentos por cancelaciones y errores de pedido se aplican en la liquidación semanal inmediatamente siguiente."),
          heading("5.5"),
          p("Las cancelaciones atribuibles al Eater o causas de fuerza mayor no generan cargo al restaurante."),
          heading("5.6"),
          p("Kusina proporcionará un registro detallado de cancelaciones, errores de pedido y devoluciones en cada liquidación."),

          sectionTitle("Cláusula Sexta: Obligaciones del Restaurante"),
          p("El restaurante se obliga a:"),
          heading("6.1"), p("Mantener permanentemente actualizado el menú, fotografías, precios, horarios y disponibilidad en la plataforma."),
          heading("6.2"), p("Ser el único y exclusivo responsable ante los Eaters y cualquier tercero por la calidad, composición, estado, condiciones bromatológicas e higiénicas, seguridad, presentación y legalidad de todos sus productos."),
          heading("6.3"), p("Cumplir las normas sanitarias, de seguridad alimentaria y demás regulaciones peruanas aplicables."),
          heading("6.4"), p("Tramitar y atender oportunamente las órdenes recibidas, preparándolas conforme a lo solicitado."),
          heading("6.5"), p("Utilizar Kusina P.O.S adecuadamente, conforme a las instrucciones proporcionadas."),
          heading("6.6"), p("Informar inmediatamente cualquier cambio en su situación legal, tributaria, operativa o comercial."),
          heading("6.7"), p("No ofrecer productos cuya venta esté prohibida o restringida en el Perú."),
          heading("6.8"), p("Contar con todos los permisos, licencias y autorizaciones vigentes requeridos por la normativa peruana."),

          sectionTitle("Cláusula Séptima: Obligaciones de Kusina"),
          p("Kusina se obliga a:"),
          heading("7.1"), p("Poner a disposición la plataforma y el P.O.S en condiciones operativas adecuadas."),
          heading("7.2"), p("Proporcionar capacitación inicial y acompañamiento a través del Pooler asignado."),
          heading("7.3"), p("Realizar los pagos en los plazos y condiciones establecidos en la Cláusula Cuarta."),
          heading("7.4"), p("Brindar soporte técnico para incidencias relacionadas con la plataforma."),
          heading("7.5"), p("Proporcionar acceso a reportes y estadísticas de ventas."),
          heading("7.6"), p("Notificar con anticipación sobre actualizaciones, mantenimientos o cambios que afecten la operación."),

          sectionTitle("Cláusula Octava: Propiedad Intelectual y Uso de Marca"),
          heading("8.1"), p("Cada parte conserva la titularidad de sus derechos de propiedad intelectual. El presente acuerdo no implica transferencia de tecnología ni de derechos."),
          heading("8.2"), p("El Restaurante autoriza a Kusina a utilizar su nombre comercial, logotipos e imágenes de sus productos para fines de exhibición en la plataforma y en materiales promocionales."),
          heading("8.3"), p("El Restaurante garantiza que las imágenes y contenidos proporcionados no infringen derechos de terceros."),

          sectionTitle("Cláusula Novena: Confidencialidad"),
          heading("9.1"), p("Las Partes se obligan a mantener en confidencialidad toda información comercial, financiera, operativa y técnica que reciban en el marco del presente acuerdo."),
          heading("9.2"), p("Esta obligación subsiste durante 2 años desde la terminación del acuerdo."),
          heading("9.3"), p("Se exceptúa la información de dominio público, la requerida por ley u orden judicial, o la conocida lícitamente con anterioridad."),

          sectionTitle("Cláusula Décima: Protección de Datos Personales"),
          heading("10.1"), p("Las Partes cumplen la Ley N.º 29733 de Protección de Datos Personales y la normativa peruana aplicable."),
          heading("10.2"), p("El Restaurante no utilizará los datos de los Eaters para fines distintos a la atención de las órdenes."),
          heading("10.3"), p("Kusina no compartirá datos del restaurante con terceros, salvo que sea necesario para la ejecución del acuerdo o por requerimiento de autoridad competente."),

          sectionTitle("Cláusula Undécima: Responsabilidad, Indemnidad y Responsabilidad Sanitaria"),
          heading("11.1 Responsabilidad General del Aliado Comercial"),
          p("El Aliado Comercial es el único y exclusivo responsable frente a los Eaters, las autoridades competentes y cualquier tercero, por todo reclamo, daño, perjuicio, sanción administrativa o acción judicial derivada de la calidad, composición, inocuidad, condiciones bromatológicas, higiénico-sanitarias, seguridad y legalidad de los productos que comercializa a través de la plataforma."),
          heading("11.2 Responsabilidad por Enfermedades Transmitidas por Alimentos"),
          p("De manera específica e irrenunciable, cualquier reclamo, denuncia, proceso administrativo sancionador ante DIGESA, SENASA, la Municipalidad competente o el INDECOPI, así como toda acción civil o penal, derivada de enfermedades transmitidas por alimentos (ETA), intoxicaciones alimentarias, alergias, contaminación microbiológica, química o física, u otros daños a la salud de los consumidores causados por el consumo de los productos del Aliado Comercial, serán de exclusiva responsabilidad de este último. Kusina, en su condición de intermediario tecnológico que no interviene en la cadena de producción, manipulación, almacenamiento ni despacho de alimentos, queda expresa y totalmente excluida de responsabilidad civil extracontractual, administrativa y penal en dichos supuestos."),
          heading("11.3 Obligación de Indemnidad"),
          p("El Aliado Comercial se obliga a defender, indemnizar y mantener indemne a KUSINA APP E.I.R.L., sus socios, directivos, empleados y representantes, frente a cualquier pretensión, demanda, multa, penalidad o gasto —incluidos honorarios legales— que se derive de: (i) el incumplimiento de las obligaciones asumidas en el presente acuerdo; (ii) la mala calidad o insalubridad de sus productos; (iii) la falta de permisos o licencias sanitarias; o (iv) cualquier acto u omisión del Aliado Comercial en el ejercicio de su actividad."),
          heading("11.4 Limitación de Responsabilidad de Kusina"),
          p("Kusina no asume responsabilidad por interrupciones temporales de la plataforma derivadas de mantenimiento programado, actualizaciones del sistema, fallas de terceros proveedores de infraestructura tecnológica, o causas de fuerza mayor o caso fortuito debidamente acreditados."),

          sectionTitle("Cláusula Duodécima: Vigencia y Terminación"),
          heading("12.1"), p("El acuerdo entra en vigencia desde su suscripción y tiene duración indefinida."),
          heading("12.2"), p("Cualquiera de las Partes puede terminar el acuerdo unilateralmente y sin expresión de causa, con un preaviso mínimo de 15 días."),
          heading("12.3"), p("Kusina puede resolver el acuerdo de forma inmediata y sin preaviso ante:"),
          bullet("Incumplimiento grave o reiterado de las obligaciones"),
          bullet("Actos que afecten la reputación o imagen de Kusina"),
          bullet("Incumplimiento de normas sanitarias o de seguridad alimentaria"),
          bullet("Fraude, falsificación o conductas ilícitas"),
          heading("12.4"), p("En caso de terminación, Kusina liquidará los montos pendientes dentro de los 15 días hábiles siguientes."),

          sectionTitle("Cláusula Decimotercera: No Exclusividad"),
          heading("13.1"), p("El acuerdo no confiere exclusividad. El restaurante es libre de comercializar a través de otras plataformas."),
          heading("13.2"), p("Kusina es libre de celebrar acuerdos similares con otros establecimientos en la misma zona geográfica."),

          sectionTitle("Cláusula Decimocuarta: Modificaciones"),
          heading("14.1"), p("Kusina puede modificar los términos del presente acuerdo notificando al Restaurante con un preaviso mínimo de 30 días."),
          heading("14.2"), p("El uso continuado de la Plataforma por parte del Restaurante después de la entrada en vigencia de las modificaciones constituirá su aceptación tácita."),
          heading("14.3"), p("Si el Restaurante no acepta las modificaciones, podrá terminar el acuerdo conforme a la Cláusula Duodécima."),

          sectionTitle("Cláusula Decimoquinta: Solución de Controversias"),
          heading("15.1"), p("Las Partes se comprometen a resolver cualquier controversia mediante negociación directa en primera instancia."),
          heading("15.2"), p("Si no se alcanza acuerdo en 15 días hábiles, las Partes podrán someter la controversia a arbitraje de derecho o a la jurisdicción de los juzgados y tribunales competentes."),

          sectionTitle("Cláusula Decimosexta: Ley Aplicable"),
          p("El presente Acuerdo se rige e interpreta conforme a las leyes de la República del Perú."),

          sectionTitle("Cláusula Decimoséptima: Disposiciones Generales"),
          heading("17.1 Acuerdo Total"), p("El presente documento constituye el acuerdo íntegro entre las Partes e invalida cualquier acuerdo previo sobre la misma materia."),
          heading("17.2 Cesión"), p("El Restaurante no puede ceder sus derechos y obligaciones sin consentimiento previo y por escrito de Kusina."),
          heading("17.3 Independencia de las Partes"), p("La relación entre las Partes es estrictamente comercial. Nada en el presente Acuerdo podrá interpretarse como la creación de una relación laboral, societaria, de agencia o de representación entre ellas."),
          heading("17.4 Nulidad Parcial"), p("La nulidad de alguna cláusula no afecta la validez de las demás disposiciones del acuerdo."),
          heading("17.5 Comunicaciones"), p("Toda comunicación debe realizarse por escrito (correo electrónico o medio físico) a las direcciones indicadas por las Partes."),

          sectionTitle("Anexo A: Condiciones Comerciales Específicas"),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              annexHeaderRow(),
              tableRow("Razón Social del Restaurante", formData.razonSocial),
              tableRow("RUC", formData.ruc),
              tableRow("Nombre Comercial", formData.nombreComercial),
              tableRow("Dirección del Establecimiento", formData.direccion),
              tableRow("Modalidad de Retribución", modalidadTexto),
              ...(formData.modalidad === "porcentual"
                ? [tableRow("Comisión Acordada", "20% fijo sobre el valor de cada orden, impuestos incluidos")]
                : [tableRow("Precio Mayorista", formData.precioMayorista || "Según lo pactado por producto")]),
              tableRow("Cuenta Bancaria para Depósitos", formData.cuentaBancaria),
              tableRow("Días de Pago",
                formData.modalidad === "porcentual"
                  ? "Lunes–Miércoles: depósito el viernes · Jueves–Domingo: depósito el miércoles siguiente"
                  : "Pago al momento de la adquisición de los productos"
              ),
              tableRow("Comprobantes al Consumidor Final",
                "Obligación exclusiva del Aliado Comercial — boleta o factura al precio de venta al público (R.S. 007-99/SUNAT)"
              ),
              tableRow("Horario de Operación en Plataforma", formData.horario),
              tableRow("Fecha de Inicio de Operaciones", formData.fechaInicio),
            ],
          }),

          p(""),
          p("Este documento constituye el acuerdo comercial entre KUSINA APP E.I.R.L. y el restaurante. Para consultas: soporte@kusina.site", { italic: true, color: "6b7280", center: true }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const fileName = `Acuerdo_Kusina_${formData.nombreComercial.replace(/\s+/g, "_")}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
